<template>
  <div class="red-history">
    <PageModal
      v-model:isOpen="isOperationsModalOpen"
      :headerModel="{
        title: $t`Операции`,
        shadowed: true,
      }"
      duration="100"
    >
      <template v-if="!$isAndroid" #head>
        <span class="text-red">{{ $t`Закрыть` }}</span>
      </template>
      <OperationDetails
        :details="operations"
        :dates="{
          from: $getFormattedDate(pickedDate.from, 'YYYY-MM-DD'),
          to: $getFormattedDate(pickedDate.to, 'YYYY-MM-DD'),
        }"
      />
    </PageModal>
    <LinkButton
      v-if="!isPlannedForClosing"
      class="red-history__calendar-link"
      @click="$router.push('/Bank/Red/PaymentCalendar?src=purchases')"
      :text="$t`Посмотреть календарь счетов`"
    />
    <Datepicker v-model:date="pickedDate">
      <template #activator="{ on }">
        <BarButton
          :title="formattedDates"
          iconHead="i-calendar"
          iconTail=""
          v-on="on"
        />
      </template>
    </Datepicker>
    <div class="operations-btn" @click="openOperationModal">
      <BarButton
        class="operations-btn__head"
        iconHead="i-operations"
        :title="$t`Операции`"
        iconTail=""
      />
      <div class="operations-btn__amount">
        <div class="operations-btn__amount_in">
          <DotsLoader v-if="isLoading" color="#58a000" />
          <template v-else>
            + {{ $currencyFormatter(replenishmentSum) }}
          </template>
        </div>
        <div class="operations-btn__amount_out">
          <DotsLoader v-if="isLoading" color="#4a4a4a" />
          <template v-else> - {{ $currencyFormatter(expenseSum) }} </template>
        </div>
      </div>
      <div class="operations-btn__icon">
        <div class="i-arrow"></div>
      </div>
    </div>
    <RedHistoryItems :loading="isLoading" :items="historyItems" />
  </div>
</template>

<script>
import dayjs from "dayjs";
import { getStatementDateFormat } from "@/utils/date";
import { fetchRedStatement } from "@/api/dashboard";
import OperationDetails from "@/components/dashboard/history/OperationDetails.vue";
import LinkButton from "@/components/shared/button/LinkButton.vue";
import BarButton from "@/components/shared/button/BarButton.vue";
import Datepicker from "@/components/shared/datepicker/CustomDatepicker.vue";
import RedHistoryItems from "@/components/dashboard/history/RedHistoryItems.vue";
import PageModal from "@/components/shared/modal/PageModal.vue";
import DotsLoader from "@/components/shared/loaders/DotsLoader.vue";

const DATE_SEND_FORMAT = "YYYY-MM-DDT00:00:00";

export default {
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
    blocked: {
      type: Boolean,
      default: false,
    },
  },
  name: "RedHistory",
  components: {
    OperationDetails,
    BarButton,
    Datepicker,
    RedHistoryItems,
    PageModal,
    DotsLoader,
    LinkButton,
  },
  emits: ["red-error", "loaded", "loading"],
  data() {
    return {
      historyItems: [],
      isLoading: true,
      isOperationsModalOpen: false,
      operations: [],
      replenishmentSum: 0,
      expenseSum: 0,
      pickedDate: {
        from: dayjs().subtract(4, "month"),
        to: dayjs(),
      },
      isPlannedForClosing: false,
    };
  },
  async created() {
    const { red_products } = await this.$store.dispatch("bundle/fetchBundle");
    this.isPlannedForClosing = red_products[0].is_planned_for_closing;
  },
  computed: {
    formattedDates() {
      return `${this.pickedDate.from.format(
        getStatementDateFormat(this.pickedDate.from)
      )} - ${this.pickedDate.to.format(
        getStatementDateFormat(this.pickedDate.to)
      )}`;
    },
  },
  methods: {
    openOperationModal() {
      if (this.historyItems.length > 0) {
        this.isOperationsModalOpen = true;
      }
    },
    async updateHistoryItems() {
      if (this.blocked) {
        this.$emit("red-error");
        return;
      }
      this.isLoading = true;
      this.$emit("loading");
      try {
        const response = (await fetchRedStatement({
          endDate: this.pickedDate.to.format(DATE_SEND_FORMAT),
          beginDate: this.pickedDate.from.format(DATE_SEND_FORMAT),
        })) || { items: [], operations: {} };
        this.historyItems = response.items;
        this.operations = response.operations;
        this.replenishmentSum = response.sum_replenishment;
        this.expenseSum = response.sum_expense;
        this.isLoading = false;

        this.$store.commit(
          "switchers/SET_PAYMENT_CHECK_SWITCHER",
          response.is_payment_receipt_enabled
        );

        this.$emit("loaded");
      } catch {
        this.$emit("red-error");
      }
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        if (val) {
          this.updateHistoryItems();
        }
      },
    },
    pickedDate() {
      this.updateHistoryItems();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/colors.scss";

.operations-btn {
  width: 100vw;
  display: flex;
  align-items: center;
  background-color: #fff;

  &__head {
    width: 50%;
    border: none;
  }

  &__amount {
    flex: 1;
    width: 200px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    height: max-content;
    border-left: 1px solid #e7e7e7;
    text-align: right;
    font-size: 14px;
    line-height: 16px;
    padding-right: 5px;

    &_in {
      padding-bottom: 5px;
      color: $colorGreen;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
  }
}

.red-history {
  padding-bottom: 5px;
  font-size: 16px;
  &__calendar-link {
    margin-bottom: 10px;
  }

  &__filter {
    &_title {
      padding: 20px 0 8px 15px;
      font-size: 12px;
      line-height: 14px;
      text-align: left;
      color: #9b9b9b;
    }
  }

  &__skeletons {
    margin-top: 10px;
  }
}
</style>
